import apiClient from "src/utils/apiClient";

import {
  ClassificationResponse,
  MatchingContainer,
  AddToContainerRequest,
  AddToContainerResponse,
  AIResearchResponse,
} from "src/types/classificationTypes";


/**
 * Get an item's classification with either its UPC or ASIN
 * GET /classification/items/${identifier}/site/{site-code}
 */
export const getItemClassification = async (
  identifier: string,
  siteCode: string
): Promise<ClassificationResponse> => {
  const response = await apiClient.get<ClassificationResponse>(
    `/classification/items/${encodeURIComponent(identifier)}/site/${encodeURIComponent(siteCode)}`
  );
  return response.data;
};


/**
 * Fetch matching containers for a site, filtered by waste profile.
 * GET /classification/containers/site/{site-code}?waste_profile_id={id}
 *
 * @param siteCode        Warehouse site code (e.g. "ABE2").
 * @param wasteProfileId  Waste profile ID to filter containers by (e.g. "HAZ-L-FLAMTOX-US").
 */
export const getMatchingContainers = async (
  siteCode: string,
  wasteProfileId: string
): Promise<MatchingContainer[]> => {
  const response = await apiClient.get<MatchingContainer[]>(
    `/classification/containers/site/${encodeURIComponent(siteCode)}`,
    { params: { waste_profile_id: wasteProfileId } }
  );
  return response.data;
};

/**
 * Add an identifier (ASIN or UPC) to a container.
 * POST /classification/add-to-container
 */
export const addToContainer = async (
  payload: AddToContainerRequest
): Promise<AddToContainerResponse> => {
  const response = await apiClient.post<AddToContainerResponse>(
    "/classification/add-to-container",
    payload
  );
  return response.data;
};

/**
 * Classify a product from user-captured images using the AI research pipeline.
 * POST /classification/ai-research  (multipart/form-data)
 *
 * @param images        One or more product image Files captured by the camera.
 * @param siteCode      Warehouse site code (e.g. "ABE2").
 * @param classificationId  Optional existing classification ID to resume a prior session.
 */
export const classifyProductFromImages = async (
  images: File[],
  siteCode: string,
  classificationId?: string,
): Promise<AIResearchResponse> => {
  const formData = new FormData();
  images.forEach((file) => formData.append("images", file));
  formData.append("site_code", siteCode);
  if (classificationId) {
    formData.append("classification_id", classificationId);
  }

  const response = await apiClient.post<AIResearchResponse>(
    "/classification/ai-research",
    formData,
  );
  return response.data;
};
